import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Camera,
  Compass,
  Crosshair,
  Database,
  Download,
  Eye,
  EyeOff,
  FileDown,
  Flag,
  Flower2,
  MapPin,
  Minus,
  Plus,
  Save,
  ScanSearch,
  SlidersHorizontal,
  X,
} from "lucide-react";
import { useSharedArCamera } from "../../camera/SharedArCamera";
import { NatureR01Panel } from "../../nature-vision/r01-private/NatureR01Panel";
import { GeoArCore } from "../lab-v14/core/GeoArCore";
import type { GeoArResult } from "../lab-v14/types/GeoArResult";
import { appendGeoArEvidence, exportGeoArEvidence, loadGeoArEvidence } from "./geoArEvidenceStore";
import { discoverGeoArLocalPackIds } from "./geoArDownloadedPacks";
import {
  appendFieldArSample,
  appendFieldEvidence,
  exportFieldSessionFiles,
  fieldSessionCounts,
  fieldSessionLinkFromRouteContext,
  type FieldArSample,
  type FieldEvidenceKind,
  type FieldEvidenceSnapshot,
  type FieldRouteContext,
} from "./fieldSessionLogger";
import {
  identifyPointedFeature,
  mergePointIdentifyResults,
  pointIdentifyLoading,
  pointIdentifyWaitingPosition,
  type PointIdentifyOutcome,
} from "./pointIdentify";
import { scanPointIdentifyCatalog } from "./pointIdentifyCatalog";
import { scanNearbyHydrology } from "./pointIdentifyNearbyHydrology";
import { scanPointIdentifyWideRange } from "./pointIdentifyWideRange";
import { auditPeakElevationIndex, type PeakElevationAuditReport } from "../../../../lib/peak-elevation-index-audit";
import { exportTextFile } from "./nativeTextExport";
import { resolveGeoArResultElevationV4 } from "./elevationResolverV4";
import {
  filterGeoArResultsByRange,
  formatGeoArRange,
  identifyPointedFeatureForRange,
  isGeoArRangeAtMaximum,
  isGeoArRangeAtMinimum,
  stepGeoArRange,
  type GeoArRangeMeters,
} from "./geoArRangeControl";
import {
  GEO_AR_NATIONAL_REGISTRY,
  SIERRA_SUR_FIELD_BETA_FEATURES,
  SIERRA_SUR_FIELD_BETA_METADATA,
} from "./geoArNationalRegistry";
import {
  requestGeoArOrientationPermission,
  useGeoArFieldOrientation,
  useGeoArFieldPosition,
} from "./useGeoArFieldSensors";

const CALIBRATION_KEY = "thera.geoar.field-beta.calibration.v1";

type Calibration = {
  horizontalFov: number;
  verticalFov: number;
  headingOffset: number;
  pitchOffset: number;
};

type Drawer = "target" | "nature" | "lab" | null;

const DEFAULT_CALIBRATION: Calibration = {
  horizontalFov: 60,
  verticalFov: 45,
  headingOffset: 0,
  pitchOffset: 0,
};

function finiteInRange(value: unknown, min: number, max: number, fallback: number) {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric >= min && numeric <= max ? numeric : fallback;
}

function normalizeCalibration(value: Partial<Calibration> | null | undefined): Calibration {
  return {
    horizontalFov: finiteInRange(value?.horizontalFov, 30, 120, DEFAULT_CALIBRATION.horizontalFov),
    verticalFov: finiteInRange(value?.verticalFov, 20, 100, DEFAULT_CALIBRATION.verticalFov),
    headingOffset: finiteInRange(value?.headingOffset, -45, 45, DEFAULT_CALIBRATION.headingOffset),
    pitchOffset: finiteInRange(value?.pitchOffset, -45, 45, DEFAULT_CALIBRATION.pitchOffset),
  };
}

function readCalibration(): Calibration {
  try {
    return normalizeCalibration(JSON.parse(localStorage.getItem(CALIBRATION_KEY) ?? "{}") as Partial<Calibration>);
  } catch {
    return { ...DEFAULT_CALIBRATION };
  }
}

function formatDistance(distance: number | null) {
  if (distance === null) return "--";
  if (distance >= 1000) return `${(distance / 1000).toFixed(2)} km`;
  return `${Math.round(distance)} m`;
}

function finiteOrDash(value: number | null, suffix = "") {
  return value === null || !Number.isFinite(value) ? "--" : `${value.toFixed(1)}${suffix}`;
}

function pointIdentifyTitle(outcome: PointIdentifyOutcome) {
  if (outcome.status === "MATCH") return outcome.result?.name ?? "Elemento geografico";
  if (outcome.status === "WAITING_POSITION") return "Esperando GPS";
  if (outcome.status === "WAITING_SENSOR") return "Esperando rumbo";
  if (outcome.status === "LOADING") return "Identificando...";
  return "Sin coincidencia";
}

function formatFeatureType(value: GeoArResult["feature_type"]) {
  switch (value) {
    case "MOUNTAIN":
    case "SUMMIT":
    case "HILL": return "cerro/montana";
    case "VOLCANO": return "volcan";
    case "LAGOON": return "laguna";
    case "LAKE": return "lago";
    case "RIVER": return "rio/quebrada";
    case "PLACE": return "lugar/poblado";
    case "VIEWPOINT": return "mirador";
    case "REFUGE": return "refugio";
    case "MOUNTAIN_PASS": return "paso de montana";
    case "GEOGRAPHIC_POINT": return "punto geografico";
    default: return "elemento geografico";
  }
}

function formatFeatureAltitude(result: GeoArResult | null | undefined) {
  const featureType = result?.feature_type ?? null;
  if (!["MOUNTAIN", "VOLCANO", "SUMMIT", "HILL"].includes(String(featureType))) return null;
  const rawAltitude = result?.feature_altitude_m;
  if (rawAltitude === null || rawAltitude === undefined) return null;
  const altitude = Number(rawAltitude);
  if (!Number.isFinite(altitude) || altitude < -500 || altitude > 9_000) return null;
  const estimated = String(result?.feature_altitude_status ?? "").startsWith("estimated-dem");
  return `${estimated ? "~" : ""}${Math.round(altitude).toLocaleString("es-EC")} msnm`;
}

function pointIdentifySummary(outcome: PointIdentifyOutcome, rangeLabel: string) {
  const altitude = formatFeatureAltitude(outcome.result);
  const parts = [
    formatFeatureType(outcome.result?.feature_type ?? null),
    altitude,
    formatDistance(outcome.result?.distance ?? null),
    `confianza ${outcome.confidenceLabel}`,
    `radio ${rangeLabel}`,
  ].filter(Boolean);
  return parts.join(" · ");
}

type GeoArFieldLabPanelProps = {
  onClose?: () => void;
  routeContext?: FieldRouteContext;
};

export function GeoArFieldLabPanel({ onClose, routeContext }: GeoArFieldLabPanelProps) {
  const camera = useSharedArCamera();
  const acquireCamera = camera.acquire;
  const releaseCameraRef = useRef<(() => void) | null>(null);
  const sessionTokenRef = useRef(0);
  const standaloneSessionKeyRef = useRef<string | null>(null);
  const lastFieldAutoSampleRef = useRef(0);
  if (!standaloneSessionKeyRef.current) standaloneSessionKeyRef.current = `geo-ar-standalone-${crypto.randomUUID()}`;
  const [active, setActive] = useState(false);
  const [drawer, setDrawer] = useState<Drawer>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [sessionNotice, setSessionNotice] = useState<string | null>(null);
  const [calibration, setCalibration] = useState<Calibration>(() => readCalibration());
  const [selectedFeatureUuid, setSelectedFeatureUuid] = useState("");
  const [results, setResults] = useState<GeoArResult[]>([]);
  const [evidenceCount, setEvidenceCount] = useState(() => loadGeoArEvidence().length);
  const [notes, setNotes] = useState("");
  const [autoLog, setAutoLog] = useState(false);
  const [pointIdentify, setPointIdentify] = useState<PointIdentifyOutcome | null>(null);
  const [pointIdentifyBusy, setPointIdentifyBusy] = useState(false);
  const [rangeMeters, setRangeMeters] = useState<GeoArRangeMeters>(null);
  const [peakElevationAudit, setPeakElevationAudit] = useState<PeakElevationAuditReport | null>(null);
  const [peakElevationAuditBusy, setPeakElevationAuditBusy] = useState(false);
  const pointIdentifyRequestRef = useRef(0);
  const rangeLabel = formatGeoArRange(rangeMeters);
  const [fieldCounts, setFieldCounts] = useState({ routeSamples: 0, arSamples: 0, evidence: 0 });

  const gps = useGeoArFieldPosition(active);
  const hasActiveRoute = Boolean(routeContext?.activityId && (routeContext.state === "recording" || routeContext.state === "paused"));
  const activeRouteContext = hasActiveRoute ? routeContext : undefined;
  const posePositionHint = gps.position ?? activeRouteContext?.track.at(-1) ?? null;
  const orientation = useGeoArFieldOrientation(active, calibration.headingOffset, calibration.pitchOffset, posePositionHint);

  const core = useMemo(() => new GeoArCore({
    async getMetadata() { return { ...SIERRA_SUR_FIELD_BETA_METADATA }; },
    async getFeatures() { return SIERRA_SUR_FIELD_BETA_FEATURES.map((feature) => ({ ...feature, aliases: [...feature.aliases] })); },
  }), []);

  const selectedFeature = selectedFeatureUuid
    ? SIERRA_SUR_FIELD_BETA_FEATURES.find((feature) => feature.feature_uuid === selectedFeatureUuid) ?? null
    : null;
  const selectedResult = selectedFeatureUuid
    ? results.find((result) => result.feature_uuid === selectedFeatureUuid) ?? null
    : null;

  const effectiveRouteContext: FieldRouteContext = activeRouteContext ?? {
    activityId: null,
    routeId: null,
    routeName: "Geo AR independiente",
    recordingMode: null,
    startedAt: null,
    state: "idle",
    distanceMeters: 0,
    track: [],
  };
  const fieldSessionLink = useMemo(() => fieldSessionLinkFromRouteContext(
    effectiveRouteContext,
    standaloneSessionKeyRef.current!,
  ), [
    effectiveRouteContext.activityId,
    effectiveRouteContext.routeId,
    effectiveRouteContext.routeName,
    effectiveRouteContext.recordingMode,
    effectiveRouteContext.startedAt,
    effectiveRouteContext.state,
  ]);
  const latestRoutePoint = activeRouteContext?.track.at(-1) ?? null;

  useEffect(() => {
    localStorage.setItem(CALIBRATION_KEY, JSON.stringify(calibration));
  }, [calibration]);

  useEffect(() => {
    setFieldCounts(fieldSessionCounts(fieldSessionLink.sessionKey));
  }, [fieldSessionLink.sessionKey]);

  useEffect(() => {
    if (!active || !gps.position) {
      setResults([]);
      return;
    }
    let cancelled = false;
    let timer: number | null = null;
    const query = async () => {
      const next = await core.query({
        position: {
          latitude: gps.position!.latitude,
          longitude: gps.position!.longitude,
          altitude: gps.position!.altitude ?? null,
          accuracy: gps.position!.accuracy,
          timestamp: new Date(gps.position!.timestamp).toISOString(),
          source_device: gps.sourceDevice,
          offline: !gps.online,
        },
        orientation: {
          heading: orientation?.heading ?? null,
          heading_accuracy: orientation?.headingAccuracy ?? null,
          pitch: orientation?.pitch ?? null,
          field_of_view: calibration.horizontalFov,
        },
      });
      if (!cancelled) setResults(next);
      if (!cancelled) timer = window.setTimeout(query, 350);
    };
    void query();
    return () => {
      cancelled = true;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [active, gps.position, gps.online, gps.sourceDevice, orientation, calibration.horizontalFov, core]);

  const createFieldArSample = useCallback((resultOverride?: GeoArResult | null): FieldArSample | null => {
    if (!gps.position || !orientation) return null;
    const result = resultOverride === undefined ? selectedResult : resultOverride;
    return {
      timestamp: Date.now(),
      online: gps.online,
      packId: "SIERRA-SUR-GEO-AR-FIELD-BETA-V1",
      position: {
        latitude: gps.position.latitude,
        longitude: gps.position.longitude,
        altitude: gps.position.altitude ?? null,
        accuracy: gps.position.accuracy,
        sourceDevice: gps.sourceDevice,
      },
      orientation,
      calibration: { ...calibration },
      selectedFeatureUuid: result?.feature_uuid ?? (selectedFeatureUuid || null),
      selectedResult: result,
    };
  }, [gps.position, gps.online, gps.sourceDevice, orientation, calibration, selectedFeatureUuid, selectedResult]);

  const createFieldEvidenceSnapshot = useCallback((resultOverride?: GeoArResult | null): FieldEvidenceSnapshot => {
    const result = resultOverride === undefined ? selectedResult : resultOverride;
    const position = gps.position
      ? {
          latitude: gps.position.latitude,
          longitude: gps.position.longitude,
          altitude: gps.position.altitude ?? null,
          accuracy: gps.position.accuracy,
          sourceDevice: gps.sourceDevice,
        }
      : latestRoutePoint
        ? {
            latitude: latestRoutePoint.latitude,
            longitude: latestRoutePoint.longitude,
            altitude: latestRoutePoint.altitude,
            accuracy: latestRoutePoint.accuracy,
            sourceDevice: "THERA_ROUTE_TRACK",
          }
        : null;
    const missingFields: string[] = [];
    if (!position) missingFields.push("GPS");
    if (!orientation) missingFields.push("orientacion");
    return {
      timestamp: Date.now(),
      online: gps.online,
      packId: "SIERRA-SUR-GEO-AR-FIELD-BETA-V1",
      position,
      orientation,
      calibration: { ...calibration },
      selectedFeatureUuid: result?.feature_uuid ?? (selectedFeatureUuid || null),
      selectedResult: result,
      completeness: missingFields.length === 0 ? "complete" : "partial",
      missingFields,
      positionSource: gps.position ? "geo-ar-gps" : latestRoutePoint ? "route-track" : "missing",
    };
  }, [gps.position, gps.online, gps.sourceDevice, latestRoutePoint, orientation, calibration, selectedFeatureUuid, selectedResult]);

  useEffect(() => {
    if (!active) return;
    const sample = createFieldArSample();
    if (!sample) return;
    if (sample.timestamp - lastFieldAutoSampleRef.current < 2_000) return;
    lastFieldAutoSampleRef.current = sample.timestamp;
    appendFieldArSample(fieldSessionLink, sample);
    setFieldCounts(fieldSessionCounts(fieldSessionLink.sessionKey));
  }, [active, createFieldArSample, fieldSessionLink]);

  const saveFieldMark = useCallback((
    kind: FieldEvidenceKind,
    identifiedResult?: GeoArResult | null,
    metadata: Record<string, string | number | boolean | null> = {},
  ) => {
    const sample = createFieldEvidenceSnapshot(identifiedResult);
    appendFieldEvidence(fieldSessionLink, sample, { kind, notes, identifiedResult: identifiedResult ?? null, metadata });
    setFieldCounts(fieldSessionCounts(fieldSessionLink.sessionKey));
    setSessionError(null);
    const labels: Record<FieldEvidenceKind, string> = {
      VISIBLE: "Visible",
      NO_VISIBLE: "No visible",
      MARK_POINT: "Punto",
      POINT_IDENTIFY: "Identificacion",
      OBSERVATION: "Observacion",
    };
    const detail = sample.completeness === "complete"
      ? sample.positionSource === "route-track" ? "guardado con GPS de la ruta THERA" : "guardado con datos completos"
      : `guardado parcial · pendiente ${sample.missingFields.join(" + ")}`;
    setSessionNotice(`${labels[kind]} ${detail}.`);
    return true;
  }, [createFieldEvidenceSnapshot, fieldSessionLink, notes]);

  const identifyAtReticle = useCallback(async () => {
    const requestId = ++pointIdentifyRequestRef.current;
    const position = gps.position ?? latestRoutePoint;
    if (!position) {
      setPointIdentifyBusy(false);
      const outcome = pointIdentifyWaitingPosition();
      setPointIdentify(outcome);
      saveFieldMark("POINT_IDENTIFY", null, {
        identifyStatus: outcome.status,
        identifyScore: outcome.score,
        identifyConfidence: outcome.confidenceLabel,
        identifyMessage: outcome.message,
        identifyPositionSource: "missing",
      });
      return;
    }
    if (!orientation || orientation.heading === null) {
      setPointIdentifyBusy(false);
      const outcome = identifyPointedFeature([], {
        horizontalAimDegrees: Math.max(6, calibration.horizontalFov * 0.14),
        verticalAimDegrees: Math.max(8, calibration.verticalFov * 0.22),
        minimumConfidence: 0.34,
        poseStable: orientation?.stableForPointIdentify ?? false,
        cameraElevationDegrees: orientation?.pitch ?? null,
      });
      setPointIdentify(outcome);
      saveFieldMark("POINT_IDENTIFY", null, {
        identifyStatus: outcome.status,
        identifyScore: outcome.score,
        identifyConfidence: outcome.confidenceLabel,
        identifyMessage: outcome.message,
        identifyPositionSource: gps.position ? "geo-ar-gps" : "route-track",
      });
      return;
    }

    setPointIdentifyBusy(true);
    setPointIdentify(pointIdentifyLoading());
    try {
      const localPackIds = await discoverGeoArLocalPackIds();
      if (pointIdentifyRequestRef.current !== requestId) return;
      const positionSource = gps.position ? "geo-ar-gps" : "route-track";
      const sidecarResults = await core.query({
        position: {
          latitude: position.latitude,
          longitude: position.longitude,
          altitude: position.altitude ?? null,
          accuracy: position.accuracy,
          timestamp: new Date(position.timestamp).toISOString(),
          source_device: gps.position ? gps.sourceDevice : "THERA_ROUTE_TRACK",
          offline: !gps.online,
        },
        orientation: {
          heading: orientation.heading,
          heading_accuracy: orientation.headingAccuracy,
          pitch: orientation.pitch,
          field_of_view: calibration.horizontalFov,
        },
      });

      let hydrologyResults: GeoArResult[] = [];
      let hydrologySource = "THERA Map Geometry";
      let hydrologyAvailable = false;
      let hydrologyPackIds: string[] = [];
      let hydrologyScannedTiles = 0;
      let hydrologyScannedFeatures = 0;
      let hydrologyCandidateFeatures = 0;
      try {
        const hydrology = await scanNearbyHydrology({
          latitude: position.latitude,
          longitude: position.longitude,
          accuracy: position.accuracy,
          heading: orientation.heading,
          headingAccuracy: orientation.headingAccuracy,
          horizontalFov: calibration.horizontalFov,
          offline: !gps.online,
          allowedPackIds: localPackIds,
          maxResults: 8,
          maxDistanceMeters: rangeMeters,
        });
        hydrologyResults = hydrology.results;
        hydrologySource = hydrology.source;
        hydrologyAvailable = hydrology.available;
        hydrologyPackIds = hydrology.packIds;
        hydrologyScannedTiles = hydrology.scannedTiles;
        hydrologyScannedFeatures = hydrology.scannedFeatures;
        hydrologyCandidateFeatures = hydrology.candidateFeatures;
      } catch {
        // V1.3 is a best-effort local PMTiles geometry adapter. The validated
        // V1.2 point/catalog flow remains the fallback if geometry is unavailable.
      }

      let catalogResults: GeoArResult[] = [];
      let catalogSource = "Geo AR sidecar";
      let catalogScanned = 0;
      let catalogNearby = 0;
      let catalogMapScanned = 0;
      let catalogMapReady = false;
      let catalogMapBuilding = false;
      let catalogAvailable = false;
      try {
        const catalog = await scanPointIdentifyCatalog({
          latitude: position.latitude,
          longitude: position.longitude,
          accuracy: position.accuracy,
          heading: orientation.heading,
          headingAccuracy: orientation.headingAccuracy,
          horizontalFov: calibration.horizontalFov,
          offline: !gps.online,
          allowedPackIds: localPackIds,
          maxResults: 12,
        });
        catalogResults = catalog.results;
        catalogSource = catalog.source;
        catalogScanned = catalog.scanned;
        catalogNearby = catalog.nearby;
        catalogMapScanned = catalog.mapCatalogScanned;
        catalogMapReady = catalog.mapCatalogReady;
        catalogMapBuilding = catalog.mapCatalogBuilding;
        catalogAvailable = true;
      } catch {
        // Point & Identify remains useful with the bundled Geo AR sidecar even
        // if the optional national gazetteer asset cannot be read.
      }

      let wideRangeResults: GeoArResult[] = [];
      let wideRangeScanned = 0;
      let wideRangeSource = "THERA Range Catalog";
      if (rangeMeters !== null && rangeMeters > 50_000) {
        try {
          const wide = await scanPointIdentifyWideRange({
            latitude: position.latitude,
            longitude: position.longitude,
            accuracy: position.accuracy,
            heading: orientation.heading,
            headingAccuracy: orientation.headingAccuracy,
            horizontalFov: calibration.horizontalFov,
            offline: !gps.online,
            maxDistanceMeters: rangeMeters,
            allowedPackIds: localPackIds,
            maxResults: 8,
          });
          wideRangeResults = wide.results;
          wideRangeScanned = wide.scanned;
          wideRangeSource = wide.source;
        } catch {
          // Manual long-range mountain search is optional. The validated V1.2/V1.3
          // flow stays available if the supplementary range catalog cannot load.
        }
      }

      if (pointIdentifyRequestRef.current !== requestId) return;
      const sidecarAndCatalog = mergePointIdentifyResults(sidecarResults, catalogResults);
      const mergedResults = mergePointIdentifyResults(hydrologyResults, sidecarAndCatalog);
      const withWideRange = mergePointIdentifyResults(mergedResults, wideRangeResults);
      const rangedResults = filterGeoArResultsByRange(withWideRange, rangeMeters);
      const baseOutcome = identifyPointedFeatureForRange(rangedResults, {
        horizontalAimDegrees: Math.max(6, calibration.horizontalFov * 0.14),
        verticalAimDegrees: Math.max(8, calibration.verticalFov * 0.22),
        minimumConfidence: 0.42,
        poseStable: orientation.stableForPointIdentify,
        cameraElevationDegrees: orientation.pitch,
      }, rangeMeters);
      let outcome = baseOutcome.status === "NO_MATCH" && catalogMapBuilding && catalogMapScanned === 0
        ? { ...baseOutcome, message: "El catalogo regional del mapa se esta preparando. Intenta de nuevo en unos segundos." }
        : baseOutcome.status === "NO_MATCH" && rangeMeters !== null
          ? { ...baseOutcome, message: `Sin coincidencias suficientemente alineadas dentro de ${rangeLabel}.` }
          : baseOutcome;

      if (outcome.status === "MATCH" && outcome.result) {
        const enrichedResult = await resolveGeoArResultElevationV4(outcome.result);
        if (pointIdentifyRequestRef.current !== requestId) return;
        if (enrichedResult) outcome = { ...outcome, result: enrichedResult };
      }
      setPointIdentify(outcome);
      saveFieldMark("POINT_IDENTIFY", outcome.result, {
        identifyStatus: outcome.status,
        identifyScore: outcome.score,
        identifyConfidence: outcome.confidenceLabel,
        identifyMessage: outcome.message,
        identifySource: outcome.sourceLabel,
        identifyFeatureType: outcome.result?.feature_type ?? null,
        identifyFeatureUuid: outcome.result?.feature_uuid ?? null,
        identifyDistance: outcome.result?.distance ?? null,
        identifyFeatureAltitudeM: outcome.result?.feature_altitude_m ?? null,
        identifyFeatureAltitudeSource: outcome.result?.feature_altitude_source ?? null,
        identifyFeatureAltitudeStatus: outcome.result?.feature_altitude_status ?? null,
        identifyBearing: outcome.result?.bearing ?? null,
        identifyPositionSource: positionSource,
        identifyOrientationSource: orientation.source,
        identifyCameraAzimuth: orientation.heading,
        identifyCameraElevation: orientation.pitch,
        identifyPoseStable: orientation.stableForPointIdentify,
        identifyTrueNorthCorrected: orientation.trueNorthCorrected,
        identifyCatalogAvailable: catalogAvailable,
        identifyCatalogSource: catalogSource,
        identifyCatalogScanned: catalogScanned,
        identifyCatalogNearby: catalogNearby,
        identifyMapCatalogScanned: catalogMapScanned,
        identifyMapCatalogReady: catalogMapReady,
        identifyMapCatalogBuilding: catalogMapBuilding,
        identifyHydrologyAvailable: hydrologyAvailable,
        identifyHydrologySource: hydrologySource,
        identifyHydrologyPackIds: hydrologyPackIds.join(","),
        identifyLocalPackIds: localPackIds.join(","),
        identifyHydrologyScannedTiles: hydrologyScannedTiles,
        identifyHydrologyScannedFeatures: hydrologyScannedFeatures,
        identifyHydrologyCandidateFeatures: hydrologyCandidateFeatures,
        identifyRangeMode: rangeMeters === null ? "AUTO" : "MANUAL",
        identifyRangeMeters: rangeMeters,
        identifyRangeLabel: rangeLabel,
        identifyWideRangeScanned: wideRangeScanned,
        identifyWideRangeSource: wideRangeSource,
      });
    } catch (cause) {
      if (pointIdentifyRequestRef.current !== requestId) return;
      const outcome: PointIdentifyOutcome = {
        status: "NO_MATCH",
        result: null,
        score: 0,
        confidenceLabel: "sin coincidencia",
        message: cause instanceof Error ? cause.message : "No fue posible completar la identificacion.",
        sourceLabel: null,
        alternatives: [],
      };
      setPointIdentify(outcome);
      saveFieldMark("POINT_IDENTIFY", null, {
        identifyStatus: outcome.status,
        identifyScore: 0,
        identifyConfidence: outcome.confidenceLabel,
        identifyMessage: outcome.message,
      });
    } finally {
      if (pointIdentifyRequestRef.current === requestId) setPointIdentifyBusy(false);
    }
  }, [
    gps.position,
    gps.online,
    gps.sourceDevice,
    latestRoutePoint,
    orientation,
    calibration.horizontalFov,
    calibration.verticalFov,
    rangeMeters,
    rangeLabel,
    core,
    saveFieldMark,
  ]);

  const stop = useCallback(() => {
    sessionTokenRef.current += 1;
    pointIdentifyRequestRef.current += 1;
    setPointIdentifyBusy(false);
    releaseCameraRef.current?.();
    releaseCameraRef.current = null;
    setActive(false);
    setAutoLog(false);
    setDrawer(null);
  }, []);

  const start = useCallback(async () => {
    const sessionToken = ++sessionTokenRef.current;
    setSessionError(null);
    setSessionNotice(null);
    try {
      const orientationGranted = await requestGeoArOrientationPermission();
      if (sessionTokenRef.current !== sessionToken) return;

      const release = await acquireCamera("geo-ar");
      if (sessionTokenRef.current !== sessionToken) {
        release();
        return;
      }

      releaseCameraRef.current?.();
      releaseCameraRef.current = release;
      setActive(true);
      if (!orientationGranted) {
        setSessionError("La camara esta activa, pero el sensor de orientacion no concedio acceso. Abre LAB para revisar el diagnostico.");
      }
    } catch (cause) {
      if (sessionTokenRef.current !== sessionToken) return;
      releaseCameraRef.current?.();
      releaseCameraRef.current = null;
      setActive(false);
      setAutoLog(false);
      setSessionError(cause instanceof Error ? cause.message : "No fue posible iniciar la camara de AR Explore.");
    }
  }, [acquireCamera]);

  useEffect(() => {
    // React StrictMode mounts effects twice in development. Delaying one tick lets the
    // first probe cleanup cancel before camera acquisition, avoiding a duplicate lease.
    const timer = window.setTimeout(() => {
      void start();
    }, 0);

    return () => {
      window.clearTimeout(timer);
      stop();
    };
  }, [start, stop]);

  const saveEvidence = (mode: "manual" | "auto" = "manual") => {
    if (gps.position && orientation) {
      const count = appendGeoArEvidence({
        schemaVersion: "1.0.0",
        checkpoint: "GEO-AR-V1.5-ANDROID-FIELD-BETA",
        observationId: crypto.randomUUID(),
        capturedAt: new Date().toISOString(),
        mode: "LAB",
        online: gps.online,
        position: {
          latitude: gps.position.latitude,
          longitude: gps.position.longitude,
          altitude: gps.position.altitude ?? null,
          accuracy: gps.position.accuracy,
          timestamp: gps.position.timestamp,
          sourceDevice: gps.sourceDevice,
        },
        orientation,
        calibration,
        selectedFeatureUuid,
        result: selectedResult,
        notes,
      });
      setEvidenceCount(count);
      if (mode === "manual") {
        saveFieldMark("OBSERVATION", selectedResult, { legacyObservationSaved: true });
      }
      setSessionError(null);
      return;
    }
    if (mode === "manual") {
      saveFieldMark("OBSERVATION", selectedResult, { legacyObservationSaved: false });
    }
  };

  useEffect(() => {
    if (!active || !autoLog) return;
    const id = window.setInterval(() => saveEvidence("auto"), 2000);
    return () => window.clearInterval(id);
  });


  const handleExportEvidence = useCallback(async () => {
    try {
      const result = await exportGeoArEvidence();
      setSessionError(null);
      setSessionNotice(`Evidencias exportadas en ${result.location}.`);
    } catch (cause) {
      setSessionNotice(null);
      setSessionError(cause instanceof Error ? cause.message : "No fue posible exportar las evidencias.");
    }
  }, []);

  const handleExportSession = useCallback(async () => {
    try {
      const result = await exportFieldSessionFiles(fieldSessionLink, effectiveRouteContext.track);
      if (!result) {
        setSessionNotice(null);
        setSessionError("No existe una sesion de campo para exportar.");
        return;
      }
      setSessionError(null);
      setSessionNotice(`Sesion exportada: ${result.exported} archivos en ${result.location}.`);
    } catch (cause) {
      setSessionNotice(null);
      setSessionError(cause instanceof Error ? cause.message : "No fue posible exportar la sesion.");
    }
  }, [fieldSessionLink, effectiveRouteContext.track]);

  const runPeakElevationAudit = useCallback(async () => {
    setPeakElevationAuditBusy(true);
    try {
      const report = await auditPeakElevationIndex();
      setPeakElevationAudit(report);
      setSessionError(null);
      setSessionNotice(`Auditoria V3 lista: ${report.totals.withElevation}/${report.totals.mountains} cerros con altura (${report.totals.coveragePercent}%).`);
    } catch (cause) {
      setSessionNotice(null);
      setSessionError(cause instanceof Error ? cause.message : "No fue posible auditar las alturas del indice V3.");
    } finally {
      setPeakElevationAuditBusy(false);
    }
  }, []);

  const exportPeakElevationAudit = useCallback(async () => {
    if (!peakElevationAudit) return;
    try {
      const stamp = peakElevationAudit.generatedAt.replace(/[:.]/g, "-");
      const result = await exportTextFile({
        fileName: `thera-peak-elevation-audit-${stamp}.json`,
        mimeType: "application/json",
        contents: JSON.stringify(peakElevationAudit, null, 2),
      });
      setSessionError(null);
      setSessionNotice(`Auditoria exportada: ${result.fileName} en ${result.location}.`);
    } catch (cause) {
      setSessionNotice(null);
      setSessionError(cause instanceof Error ? cause.message : "No fue posible exportar la auditoria de alturas.");
    }
  }, [peakElevationAudit]);

  const relativeAngle = selectedResult?.relative_angle ?? null;
  const horizontalVisible = relativeAngle !== null && Math.abs(relativeAngle) <= calibration.horizontalFov / 2;
  const x = relativeAngle === null ? 50 : Math.min(96, Math.max(4, 50 + (relativeAngle / calibration.horizontalFov) * 100));
  const relativeVertical = selectedResult?.relative_vertical_angle ?? null;
  const y = relativeVertical === null ? 46 : Math.min(90, Math.max(10, 50 - (relativeVertical / calibration.verticalFov) * 100));

  const openDrawer = (next: Exclude<Drawer, null>) => {
    setDrawer((current) => current === next ? null : next);
  };

  return (
    <section className="thera-geoar-field-lab" aria-label="Geo AR Field LAB">
      <div className="thera-geoar-camera-overlay" aria-live="polite">
        {active && horizontalVisible && selectedResult ? (
          <div className="thera-geoar-poi-label" style={{ left: `${x}%`, top: `${y}%` }}>
            <MapPin />
            <span>
              <strong>{selectedResult.name}</strong>
              <small>{formatDistance(selectedResult.distance)} · {finiteOrDash(selectedResult.bearing, "°")}</small>
              <small>{selectedFeature?.altitude == null ? "Altitud pendiente" : `${selectedFeature.altitude} m`}</small>
            </span>
          </div>
        ) : null}

        {active ? (
          <button
            type="button"
            className="thera-geoar-crosshair thera-point-identify-trigger"
            onClick={() => void identifyAtReticle()}
            disabled={pointIdentifyBusy}
            aria-label="Apuntar e identificar elemento geografico"
          >
            <span aria-hidden="true">+</span>
          </button>
        ) : null}

        {active ? (
          <div className="thera-geoar-range-control" aria-label="Radio de busqueda Geo AR">
            <button
              type="button"
              className={rangeMeters === null ? "thera-geoar-range-auto active" : "thera-geoar-range-auto"}
              onClick={() => setRangeMeters(null)}
              aria-pressed={rangeMeters === null}
              aria-label="Usar radio automatico"
            >AUTO</button>
            <button
              type="button"
              onClick={() => setRangeMeters((value) => stepGeoArRange(value, 1))}
              disabled={isGeoArRangeAtMaximum(rangeMeters)}
              aria-label="Aumentar radio de busqueda"
            ><Plus /></button>
            <span className="thera-geoar-range-value" aria-live="polite">{rangeLabel}</span>
            <button
              type="button"
              onClick={() => setRangeMeters((value) => stepGeoArRange(value, -1))}
              disabled={rangeMeters === null || isGeoArRangeAtMinimum(rangeMeters)}
              aria-label="Disminuir radio de busqueda"
            ><Minus /></button>
          </div>
        ) : null}

        {active && pointIdentify ? (
          <div className={`thera-point-identify-result ${pointIdentify.status.toLowerCase()}`}>
            <Crosshair />
            <span>
              <strong>{pointIdentifyTitle(pointIdentify)}</strong>
              <small>{pointIdentify.status === "MATCH"
                ? pointIdentifySummary(pointIdentify, rangeLabel)
                : pointIdentify.message}</small>
            </span>
          </div>
        ) : null}
      </div>

      {drawer ? (
        <aside className={`thera-ar-drawer thera-ar-drawer-${drawer}`} aria-label={`Panel ${drawer}`}>
          <div className="thera-ar-drawer-head">
            <strong>{drawer === "target" ? "Objetivo" : drawer === "nature" ? "Nature" : "LAB"}</strong>
            <button type="button" className="icon" onClick={() => setDrawer(null)} aria-label="Cerrar panel"><X /></button>
          </div>

          {drawer === "target" ? (
            <div className="thera-ar-target-panel">
              <label>
                Punto a identificar
                <select value={selectedFeatureUuid} onChange={(event) => setSelectedFeatureUuid(event.target.value)}>
                  <option value="">Sin objetivo manual</option>
                  {SIERRA_SUR_FIELD_BETA_FEATURES.map((feature) => (
                    <option key={feature.feature_uuid} value={feature.feature_uuid}>{feature.name}</option>
                  ))}
                </select>
              </label>
              <button type="button" className="thera-point-identify-button" onClick={() => void identifyAtReticle()} disabled={!active || pointIdentifyBusy}>
                <ScanSearch /> {pointIdentifyBusy ? "Identificando..." : "Apuntar e identificar"}
              </button>
              <p className="thera-point-identify-help">La reticula consulta el sidecar Geo AR, la geometria cercana de rios/quebradas y los nombres reales de tus mapas THERA descargados. No reconoce por imagen. El control lateral modifica solo el radio geografico; no hace zoom ni cambia el FOV.</p>
              {pointIdentify ? (
                <div className="thera-point-identify-panel-result">
                  <strong>{pointIdentifyTitle(pointIdentify)}</strong>
                  <span>{pointIdentify.message}</span>
                  {pointIdentify.status === "MATCH" ? (
                    <>
                      <small>{pointIdentifySummary(pointIdentify, rangeLabel)}</small>
                      {formatFeatureAltitude(pointIdentify.result) ? (
                        <small>Altitud desde {pointIdentify.result?.feature_altitude_source ?? "metadato cartografico"}</small>
                      ) : null}
                      <small>Fuente {pointIdentify.sourceLabel ?? "Geo AR"}</small>
                      {pointIdentify.alternatives.length ? <small>Alternativas cercanas: {pointIdentify.alternatives.join(", ")}</small> : null}
                    </>
                  ) : null}
                </div>
              ) : null}
              {selectedFeature ? (
                <>
                  <div className="thera-ar-target-summary">
                    <span><strong>Distancia</strong>{formatDistance(selectedResult?.distance ?? null)}</span>
                    <span><strong>Bearing</strong>{finiteOrDash(selectedResult?.bearing ?? null, "°")}</span>
                    <span><strong>Fuente</strong>{selectedFeature.source}</span>
                    <span><strong>Estado</strong>{selectedFeature.verification_level}</span>
                  </div>
                  <p className="thera-ar-reference-note">Objetivo manual activo. Cerro Minas y Paraguillas siguen como referencias hasta validar evidencia fisica.</p>
                </>
              ) : (
                <p className="thera-ar-reference-note">Sin objetivo manual. Usa la reticula + para identificar elementos del catalogo real de los mapas THERA.</p>
              )}
            </div>
          ) : null}

          {drawer === "nature" ? (
            <div className="thera-ar-nature-panel">
              <NatureR01Panel />
            </div>
          ) : null}

          {drawer === "lab" ? (
            <div className="thera-ar-lab-panel">
              {(sessionError || gps.error) ? (
                <div className="thera-geoar-warning">
                  {sessionError ? <p>{sessionError}</p> : null}
                  {gps.error ? <p>GPS: {gps.error}</p> : null}
                </div>
              ) : null}

              {sessionNotice ? (
                <div className="thera-field-save-notice" role="status">
                  {sessionNotice}
                </div>
              ) : null}

              <div className="thera-geoar-actions">
                <button type="button" onClick={() => saveEvidence("manual")} disabled={!active}><Save /> Guardar observacion</button>
                <button type="button" onClick={() => void handleExportEvidence()} disabled={evidenceCount === 0}><Download /> Exportar ({evidenceCount})</button>
                <button
                  type="button"
                  onClick={() => void handleExportSession()}
                  disabled={fieldCounts.routeSamples + fieldCounts.arSamples + fieldCounts.evidence === 0}
                ><FileDown /> Exportar sesion</button>
              </div>

              <div className="thera-field-session-status">
                <strong>Field Session Logger V1</strong>
                <span>{effectiveRouteContext.activityId ? `Ruta vinculada · ${effectiveRouteContext.routeName}` : "Sesion AR independiente · no requiere ruta"}</span>
                <small>{fieldCounts.routeSamples} ruta · {fieldCounts.arSamples} AR · {fieldCounts.evidence} evidencias</small>
              </div>

              <div className="thera-field-evidence-actions" aria-label="Evidencia de campo">
                <button type="button" onClick={() => saveFieldMark("VISIBLE")} disabled={!active}><Eye /> Visible</button>
                <button type="button" onClick={() => saveFieldMark("NO_VISIBLE")} disabled={!active}><EyeOff /> No visible</button>
                <button type="button" onClick={() => saveFieldMark("MARK_POINT")} disabled={!active}><Flag /> Marcar punto</button>
              </div>

              <div className="thera-geoar-field-grid">
                <article><Camera /><strong>Camara</strong><small>{camera.status === "live" ? "En vivo" : camera.status}</small></article>
                <article><Compass /><strong>Orientacion</strong><small>{finiteOrDash(orientation?.heading ?? null, "°")} · elev {finiteOrDash(orientation?.pitch ?? null, "°")}</small><small>{orientation?.source === "ANDROID_ROTATION_VECTOR_CAMERA_FORWARD" ? "Camara · Rotation Vector" : "DeviceOrientation"}</small></article>
                <article><MapPin /><strong>GPS THERA</strong><small>{gps.position
                  ? `±${Math.round(gps.position.accuracy)} m`
                  : latestRoutePoint
                    ? `Ruta ±${Math.round(latestRoutePoint.accuracy)} m`
                    : "Esperando"}</small></article>
                <article><Database /><strong>Sidecar</strong><small>Sierra Sur · {gps.online ? "online" : "offline"}</small></article>
              </div>

              <div className="thera-calibration-summary" aria-label="Calibracion base activa">
                <strong>Calibracion automatica</strong>
                <small>{calibration.horizontalFov}° x {calibration.verticalFov}° · offsets {calibration.headingOffset}° / {calibration.pitchOffset}°</small>
              </div>

              <details className="thera-ar-lab-section">
                <summary>Auditor alturas · indice V3</summary>
                <div className="thera-geoar-actions">
                  <button type="button" onClick={() => void runPeakElevationAudit()} disabled={peakElevationAuditBusy}>
                    <Database /> {peakElevationAuditBusy ? "Auditando..." : "Auditar indice V3"}
                  </button>
                  <button type="button" onClick={() => void exportPeakElevationAudit()} disabled={!peakElevationAudit || peakElevationAuditBusy}>
                    <FileDown /> Exportar auditoria
                  </button>
                </div>
                {peakElevationAudit ? (
                  <>
                    <div className="thera-geoar-diagnostics">
                      <span>Indice esperado: V{peakElevationAudit.expectedIndexVersion}</span>
                      <span>Indices listos: {peakElevationAudit.index.readyCount}/{peakElevationAudit.index.indexedCount}</span>
                      <span>Preparando: {peakElevationAudit.index.building ? `si · ${peakElevationAudit.index.progress}%` : "no"}</span>
                      <span>Cerros/montanas: {peakElevationAudit.totals.mountains}</span>
                      <span>Con nombre: {peakElevationAudit.totals.namedMountains}</span>
                      <span>Con altura: {peakElevationAudit.totals.withElevation}</span>
                      <span>Sin altura: {peakElevationAudit.totals.withoutElevation}</span>
                      <span>Alturas invalidas: {peakElevationAudit.totals.invalidElevation}</span>
                      <span>Cobertura: {peakElevationAudit.totals.coveragePercent}%</span>
                      <span>Packs: {peakElevationAudit.index.mapPackIds.join(", ") || "ninguno"}</span>
                    </div>
                    <p className="thera-ar-reference-note">
                      {peakElevationAudit.withElevation.length
                        ? `Con altura: ${peakElevationAudit.withElevation.slice(0, 6).map((item) => `${item.name} ${Math.round(item.elevationM ?? 0).toLocaleString("es-EC")} m`).join(" · ")}`
                        : "No se encontraron cerros con altura en los indices V3 disponibles."}
                    </p>
                    <p className="thera-ar-reference-note">
                      {peakElevationAudit.withoutElevation.length
                        ? `Sin altura: ${peakElevationAudit.withoutElevation.slice(0, 8).map((item) => item.name || "sin nombre").join(" · ")}`
                        : "Todos los cerros auditados tienen altura valida."}
                    </p>
                    {peakElevationAudit.packs.length ? (
                      <div className="thera-geoar-diagnostics">
                        {peakElevationAudit.packs.slice(0, 8).map((pack) => (
                          <span key={pack.mapPackId}>{pack.mapPackId}: {pack.withElevation}/{pack.mountains} con altura · {pack.coveragePercent}%</span>
                        ))}
                      </div>
                    ) : null}
                  </>
                ) : (
                  <p className="thera-ar-reference-note">Lee el indice offline V3 ya generado por THERA. No modifica PMTiles ni rellena alturas faltantes.</p>
                )}
              </details>

              <details className="thera-ar-lab-section">
                <summary>Calibracion avanzada</summary>
                <div className="thera-geoar-controls">
                  <label>FOV horizontal<input type="number" min="30" max="120" step="1" value={calibration.horizontalFov} onChange={(event) => setCalibration((value) => ({ ...value, horizontalFov: Number(event.target.value) }))} /></label>
                  <label>FOV vertical<input type="number" min="20" max="100" step="1" value={calibration.verticalFov} onChange={(event) => setCalibration((value) => ({ ...value, verticalFov: Number(event.target.value) }))} /></label>
                  <label>Offset heading<input type="number" min="-45" max="45" step="0.5" value={calibration.headingOffset} onChange={(event) => setCalibration((value) => ({ ...value, headingOffset: Number(event.target.value) }))} /></label>
                  <label>Offset pitch<input type="number" min="-45" max="45" step="0.5" value={calibration.pitchOffset} onChange={(event) => setCalibration((value) => ({ ...value, pitchOffset: Number(event.target.value) }))} /></label>
                  <div className="thera-geoar-actions thera-calibration-actions">
                    <button type="button" onClick={() => setCalibration({ ...DEFAULT_CALIBRATION })}>Usar base automatica 60 x 45</button>
                  </div>
                  <label className="thera-geoar-checkbox"><input type="checkbox" checked={autoLog} disabled={!active} onChange={(event) => setAutoLog(event.target.checked)} />Auto registro evidencia legado cada 2 s</label>
                  <label className="thera-geoar-notes">Nota de campo<input value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Ej.: etiqueta 8° a la derecha" /></label>
                </div>
              </details>

              <details className="thera-ar-lab-section">
                <summary>Diagnostico</summary>
                <div className="thera-geoar-diagnostics">
                  <span>Target manual: {selectedFeature?.name ?? "ninguno"}</span>
                  <span>Radio Geo AR: {rangeLabel}</span>
                  <span>Fuente: {selectedFeature?.source ?? "--"}</span>
                  <span>Licencia: {selectedFeature?.license ?? "--"}</span>
                  <span>Verificacion: {selectedFeature?.verification_level ?? "--"}</span>
                  <span>Distancia: {formatDistance(selectedResult?.distance ?? null)}</span>
                  <span>Bearing: {finiteOrDash(selectedResult?.bearing ?? null, "°")}</span>
                  <span>Angulo relativo: {finiteOrDash(selectedResult?.relative_angle ?? null, "°")}</span>
                  <span>GPS: {gps.position ? `±${Math.round(gps.position.accuracy)} m` : "--"}</span>
                  <span>Heading accuracy: {finiteOrDash(orientation?.headingAccuracy ?? null, "°")}</span>
                  <span>Roll: {finiteOrDash(orientation?.roll ?? null, "°")}</span>
                  <span>Rotacion pantalla: {orientation?.screenRotation ?? "--"}</span>
                  <span>Sensor absoluto: {orientation?.absolute ? "si" : "no/pendiente"}</span>
                  <span>Field session: {fieldSessionLink.sessionKey}</span>
                  <span>Ruta vinculada: {effectiveRouteContext.activityId ?? "sin ruta"}</span>
                  <span>Muestras AR: {fieldCounts.arSamples}</span>
                  <span>Evidencias: {fieldCounts.evidence}</span>
                </div>
              </details>

              <details className="thera-ar-lab-section">
                <summary>Sidecars nacionales</summary>
                <div className="thera-geoar-registry-grid">
                  {GEO_AR_NATIONAL_REGISTRY.map((entry) => (
                    <span key={entry.mapPackId}>{entry.mapPackId}: {entry.status}{entry.bundledOffline ? " · offline" : ""}</span>
                  ))}
                </div>
              </details>
            </div>
          ) : null}
        </aside>
      ) : null}

      <nav className={`thera-ar-toolbar thera-ar-toolbar-direct${onClose ? " has-close" : ""}`} aria-label="Controles AR">
        <button type="button" className={drawer === "target" ? "active" : ""} onClick={() => openDrawer("target")}><MapPin /><span>Objetivo</span></button>
        <button type="button" className={drawer === "nature" ? "active" : ""} onClick={() => openDrawer("nature")}><Flower2 /><span>Nature</span></button>
        <button type="button" className={drawer === "lab" ? "active" : ""} onClick={() => openDrawer("lab")}><SlidersHorizontal /><span>LAB</span></button>
        {onClose ? (
          <button
            type="button"
            className="thera-ar-context-close"
            onClick={onClose}
            aria-label="Cerrar AR y volver a la navegación principal"
          >
            <X />
          </button>
        ) : null}
      </nav>
    </section>
  );
}
